// components/Modal.js
import React from 'react';
import { COLORS, RADIUS, SPACING, TYPE, MODAL_WIDTH, Z_INDEX, SHADOW, TAP_MIN, safeAreaPadding } from '../theme';
import useIsMobile from '../useIsMobile';

// The one shared shape for every pop-up dialog in the app — replaces 36 hand-rolled
// overlay+card implementations that had drifted on close-button presence, width, z-index, and
// (the reported bug) corner rounding. The fix for that last one is structural: the outer wrapper
// owns borderRadius + overflow: hidden but never scrolls itself; the inner body owns
// overflowY: auto instead. A browser-rendered scrollbar sits flush against whatever element it's
// attached to — putting it on an un-rounded inner div means it can never square off a corner.
// `footer` (optional) stays pinned below the scrolling body via flex, so Save/Cancel/Delete never
// scroll out of reach on a tall form the way they could when the whole card scrolled as one unit.
//
// On mobile (Ops Console Phase 5) this becomes a full-screen sheet instead of a centered card —
// no backdrop margin, no corner rounding, no maxWidth cap — since a centered 400px-wide card on a
// 375px-wide phone leaves almost no usable width and the backdrop reads as wasted space.
//
// Because that sheet owns every screen edge, it has to carry the hardware safe-area insets itself.
// It did not, and index.html's viewport-fit=cover means the viewport runs underneath the status
// bar: the title and the "×" rendered beneath the clock on a notched phone, so the close button
// could not be tapped. That was true of all 50 screens using this component at once, which is
// also why fixing it here rather than at any call site is the whole point of having it.
//
// Deliberately does NOT close on a backdrop click or Escape — only the "×" (or an in-form
// Cancel/Save) can dismiss it. Confirmed real data loss: an accidental click just outside a
// modal, or a reflexive Escape press mid-form, used to silently discard everything typed so far
// (e.g. a work order half filled out). All 44 files using this component already rely on this
// as the only close path, so there's nothing to preserve by keeping either shortcut.
function Modal({ title, onClose, maxWidth = 'md', zIndex = 'modal', footer = undefined, children }) {
  const isMobile = useIsMobile();

  return (
    <div
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center',
        zIndex: Z_INDEX[zIndex],
        ...safeAreaPadding(isMobile, SPACING.xl)
      }}
    >
      {/* role/aria-modal added 2026-09-16: correct markup for a dialog (screen readers announce it
          as one and stop reading the page behind it), and it gives every modal in the app a stable
          hook for browser tests, which previously had only the close button's test id to aim at. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        data-testid="modal"
        style={{
          backgroundColor: COLORS.white, borderRadius: isMobile ? 0 : RADIUS.lg, overflow: 'hidden',
          width: '100%', maxWidth: isMobile ? '100%' : MODAL_WIDTH[maxWidth],
          maxHeight: isMobile ? '100%' : '90vh', display: 'flex', flexDirection: 'column',
          boxShadow: isMobile ? 'none' : SHADOW.modal
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: SPACING.md, padding: `${SPACING.xxl} ${SPACING.xxl} 0`, flexShrink: 0 }}>
          <h3 style={{ margin: 0, fontSize: TYPE.h3, fontWeight: '700', color: COLORS.text }}>{title}</h3>
          {/* 28px on a desktop pointer, TAP_MIN on a phone. It was a flat 28 everywhere, which is
              a comfortable mouse target and well under the 44px a thumb needs — and this is the
              one control every modal in the app shares, so the miss was everywhere at once.
              Resolved here rather than at the call sites, the same way Phase 5 handled Button and
              the other five shared components. */}
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="modal-close-button"
            style={{ flexShrink: 0, width: isMobile ? TAP_MIN : '28px', height: isMobile ? TAP_MIN : '28px', border: 'none', background: 'none', borderRadius: RADIUS.sm, color: COLORS.textFaint, fontSize: '18px', cursor: 'pointer', lineHeight: 1 }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: SPACING.xxl, overflowY: 'auto', flex: 1 }}>
          {children}
        </div>
        {footer && (
          <div style={{ padding: SPACING.xxl, borderTop: `1px solid ${COLORS.border}`, display: 'flex', gap: SPACING.md, flexShrink: 0 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export default Modal;
